import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card, Icon, linkTextClasses } from '@evogard/ui';
import { OPEN_STATUSES, ticketPriorityLabel } from '@evogard/domain';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { Deadline, TicketStatusBadge } from './common.js';

/** Quantas cabem no cartão sem ele crescer mais que o vizinho da coluna. */
const SHOWN = 5;

/**
 * As demandas em aberto que estão COM a pessoa — o "Comigo" do painel de
 * números, aberto em linhas.
 *
 * O recorte é o `mine=comigo` da mesa, resolvido no servidor por
 * `conditionsOf`: o cartão lista exatamente o que o número "Comigo" conta, e
 * "Ver todas" leva à mesa no mesmo recorte. Filtrar no navegador daria uma
 * lista que discorda do número três centímetros acima.
 *
 * Só as em aberto (`OPEN_STATUSES`): uma concluída ontem não é trabalho de
 * ninguém, e a lista precisa caber em cinco linhas.
 */
export function MyTicketsPanel() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  const query = useQuery({
    queryKey: ['demandas', 'comigo', tenantId],
    queryFn: async () => {
      const r = await api.tickets.list({
        query: {
          page: 1,
          perPage: SHOWN,
          mine: 'comigo',
          situacao: OPEN_STATUSES.join(','),
        },
      });
      if (r.status !== 200) throw new Error('Não foi possível carregar os tickets.');
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const rows = query.data?.items;

  return (
    <Card
      className="h-full"
      title={
        <span className="flex items-center gap-2">
          <Icon nome="menu" className="text-texto-suave" />
          Meus tickets
        </span>
      }
      descricao="As que estão com você, em aberto."
      actions={
        <Link
          to="/tickets"
          search={{ mine: 'comigo' }}
          className={linkTextClasses('gap-1 text-xs')}
        >
          Ver todos <Icon nome="seta-direita" />
        </Link>
      }
    >
      {query.isError ? (
        <p className="text-sm text-texto-suave">
          Não foi possível ler a lista agora. A mesa continua acessível.
        </p>
      ) : rows === undefined ? (
        /* esqueleto, e não "nenhuma": a lista vazia é um fato, e ainda não se sabe */
        <ul className="flex flex-col gap-2" aria-busy="true">
          {Array.from({ length: 3 }, (_, i) => (
            <li
              key={i}
              className="h-12 animate-pulse rounded-borda bg-superficie-suave"
            />
          ))}
        </ul>
      ) : rows.length === 0 ? (
        <p className="text-sm text-texto-suave">
          Nenhum ticket em aberto com você nesta base.
        </p>
      ) : (
        <ul className="flex flex-col">
          {rows.map((d) => (
            <li
              key={d.id}
              className="flex items-start justify-between gap-4 border-b border-borda-suave py-2.5 first:pt-0 last:border-0 last:pb-0"
            >
              <div className="min-w-0">
                <Link
                  to="/tickets/$id"
                  params={{ id: String(d.id) }}
                  className="block truncate text-sm font-medium text-texto hover:underline pointer-coarse:py-3"
                >
                  {d.title}
                </Link>
                <p className="mt-0.5 text-xs text-texto-suave">
                  {d.categoria?.nome ?? 'Sem categoria'}
                  {/* `normal` não se diz — o mesmo critério do selo no detalhe */}
                  {d.priority !== 'normal' &&
                    ` · Prioridade ${ticketPriorityLabel(d.priority).toLowerCase()}`}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
                <TicketStatusBadge situacao={d.situacao} />
                <Deadline dueAt={d.dueAt} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
